import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lock, RefreshCw, Eye, EyeOff } from 'lucide-react';

function PasswordField({ id, label, value, onChange, show, onToggle, minLength, testId, toggleTestId, helpText }) {
  return (
    <div>
      <Label htmlFor={id} className="text-sm font-medium text-slate-700">{label}</Label>
      <div className="relative mt-1">
        <Input
          id={id}
          type={show ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          className="pr-10"
          required
          minLength={minLength}
          data-testid={testId}
        />
        <button
          type="button"
          tabIndex={-1}
          onClick={onToggle}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          data-testid={toggleTestId}
        >
          {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
      {helpText ? <p className="text-xs text-slate-500 mt-1">{helpText}</p> : null}
    </div>
  );
}

/**
 * Change-password form. All state lives in the parent.
 */
export default function ChangePasswordForm({
  passwordData,
  onChange,
  onSubmit,
  changingPassword,
  showPasswords,
  onTogglePassword,
}) {
  const toggle = (key) => onTogglePassword(prev => ({ ...prev, [key]: !prev[key] }));
  return (
    <Card className="border border-slate-200 shadow-sm mt-8">
      <div className="p-6 border-b border-slate-200">
        <div className="flex items-center">
          <Lock className="w-6 h-6 text-slate-600 mr-3" />
          <div>
            <h2 className="text-xl font-semibold text-slate-900">Change Password</h2>
            <p className="text-sm text-slate-500">Update your account password</p>
          </div>
        </div>
      </div>

      <div className="p-6">
        <form onSubmit={onSubmit} className="space-y-4 max-w-md">
          <PasswordField
            id="current_password"
            label="Current Password"
            value={passwordData.current_password}
            onChange={(e) => onChange({ ...passwordData, current_password: e.target.value })}
            show={showPasswords.current_password}
            onToggle={() => toggle('current_password')}
            testId="current-password-input"
            toggleTestId="toggle-current-password"
          />
          <PasswordField
            id="new_password"
            label="New Password"
            value={passwordData.new_password}
            onChange={(e) => onChange({ ...passwordData, new_password: e.target.value })}
            show={showPasswords.new_password}
            onToggle={() => toggle('new_password')}
            minLength={14}
            testId="new-password-input"
            toggleTestId="toggle-mfa-new-password"
            helpText="Must be at least 14 characters (max 64). Cannot reuse your last 5 passwords."
          />
          <PasswordField
            id="confirm_password"
            label="Confirm New Password"
            value={passwordData.confirm_password}
            onChange={(e) => onChange({ ...passwordData, confirm_password: e.target.value })}
            show={showPasswords.confirm_password}
            onToggle={() => toggle('confirm_password')}
            testId="confirm-password-input"
            toggleTestId="toggle-mfa-confirm-password"
          />
          <Button
            type="submit"
            disabled={changingPassword}
            className="bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="change-password-button"
          >
            {changingPassword ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Changing...
              </>
            ) : (
              <>
                <Lock className="w-4 h-4 mr-2" />
                Change Password
              </>
            )}
          </Button>
        </form>
      </div>
    </Card>
  );
}
